import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactDOM from 'react-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PeopleIcon from '@mui/icons-material/People';
import {
  GOT_TOURNAMENT_REBUY,
  GOT_TOURNAMENT_REGISTER,
  GOT_TOURNAMENT_UNREGISTER,
  UNSUBCRIBE_TO_GAME_UPDATES
} from 'constants/eventConstants';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { TABLE_TRANSITION_MS, ViewManagerContext } from 'contexts/ViewManagerContext';
import { useFeatureFlag } from 'featureFlags';
import gsap from 'gsap';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';
import { isFreezeoutGameType } from 'hooks/useIsFreezeout';
import { useSngPayouts } from 'hooks/useSngPayouts';
import { clearTournamentStatusFor } from 'reducers/currentTable';
import { TableType } from 'reducers/lobby';
import { updatePlayerGames } from 'reducers/playerGames';
import { removeTable } from 'reducers/tables';
import useGameService from 'services/GameService';
import { markTournamentIntentBeforeLogin, redirectToLogin } from 'utils/auth';
import {
  CONNECTION_LOST_TITLE,
  connectionLostMessage,
  isBuyInConnectionLost
} from 'utils/connectionLost';
import { currencyCompact, currencyFormatter, humanizeCountdown } from 'utils/functions';
import { decompressData } from 'utils/functions';
import { GUEST_CTA } from 'utils/guestCta';
import { markRecentlyExitedTable } from 'utils/recentlyExitedTables';

import { CashierContext } from 'components/Cashier/CashierContext';
import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import GameModal, {
  btnRowStyle,
  dangerBtnStyle,
  infoRowStyle,
  labelStyle as modalLabelStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  valueStyle as modalValueStyle
} from 'components/GameModal/GameModal';
import { sitGoCardName } from 'components/SaloonLobby/sitGoBuyInSections';
import TournamentDetails from 'components/Table/TournamentDetails';
import { payoutPool } from 'components/TournamentPayouts/payouts';
import TournamentPayouts from 'components/TournamentPayouts/TournamentPayouts';

import {
  isAlreadyRegisteredReply,
  isRegistrationClosedRefusal,
  NO_OPEN_TABLE_NOTICE
} from './registrationClosedRefusal';
import {
  awaitRematchRegistration,
  claimRematchRegistration,
  releaseRematchRegistration,
  RematchRegistrationFailure,
  settleRematchRegistration
} from './rematchRegistrationsInFlight';

type TournamentDetailModalProps = {
  open: boolean;
  onClose: () => void;
  tournament: any | null;
  /** True for the one open triggered by the post-login resume flow (see
   * SaloonLobbyContainer) — advances straight to the register confirm step. */
  autoRegisterOnOpen?: boolean;
  /** Notifies the parent once the auto-register attempt has run, so it only fires once. */
  onAutoRegisterConsumed?: () => void;
  /**
   * EN-11109: opened from a finished Sit & Go's rematch offer (Yes), not the
   * lobby. Unset, every path below is exactly as before.
   */
  rematch?: SitGoRematchOptions;
};

export type SitGoRematchOptions = {
  /** The finished table, replaced by the one this registers into. */
  leavingGameID: string;
  /**
   * Cancel (or the X) on Confirm Registration, and Cancel / Close / the X on
   * Registration Failed: back to the result offer, not to the detail view.
   */
  onCancel: () => void;
  /** Registered, and the new table is opening: the finished table follows it out. */
  onRegistered: () => void;
  /**
   * The template's next open table from the latest lobby list, skipping the
   * tournaments given, or null if none is listed. Asked whenever the engine
   * refuses the registration because the table filled up meanwhile
   * (isRegistrationClosedRefusal), and while none is listed yet.
   */
  findNextTable?: (excludeTournamentIds: string[]) => any | null;
  /**
   * "Go to lobby" on the No open table notice, once the rematch has given up
   * looking for a table: the lobby's Sit & Go tab.
   */
  onNoOpenTable?: () => void;
};

/**
 * EN-11109 (Jason, 2026-10-27): when a Play Again's table fills up first, the
 * registration moves on, with no second Confirm, up to this many times, and
 * looks for a listed table for up to REMATCH_SEARCH_MS in all. Then it gives
 * up with the No open table notice, never asking for Play Again again.
 */
export const REMATCH_MAX_MOVES = 3;
export const REMATCH_SEARCH_MS = 30_000;
/** How often the search re-reads the lobby list while no table is listed. */
const REMATCH_SEARCH_POLL_MS = 1_000;

/**
 * EN-11109: open a registered rematch's table, then run `onOpened` (which
 * retires the finished one). openTable refuses during a table switch's
 * transition, so a refusal is tried once more after it; `onRefused` runs if
 * that try is refused too. Any pending try is dropped first, so the seat-first
 * path and the reply path never both open the table.
 */
const openRematchTable = (
  openNext: () => boolean,
  onOpened: () => void,
  retryRef: React.MutableRefObject<number | undefined>,
  onRefused?: () => void
) => {
  window.clearTimeout(retryRef.current);
  retryRef.current = undefined;
  if (openNext()) {
    onOpened();
    return;
  }
  retryRef.current = window.setTimeout(() => {
    retryRef.current = undefined;
    if (openNext()) onOpened();
    else onRefused?.();
  }, TABLE_TRANSITION_MS + 50);
};

/** EN-11307: a Register Confirm while the action socket is down (utils/connectionLost). */
const CONNECTION_LOST_FAILURE: RematchRegistrationFailure = {
  title: CONNECTION_LOST_TITLE,
  message: connectionLostMessage('register'),
  insufficientFunds: false
};

const statusColors: Record<string, { bg: string; text: string }> = {
  A: { bg: 'rgba(41, 128, 185, 0.2)', text: '#5dade2' },
  R: { bg: 'rgba(39, 174, 96, 0.2)', text: '#58d68d' },
  P: { bg: 'rgba(233, 140, 38, 0.2)', text: '#E98C26' },
  F: { bg: 'rgba(100, 100, 100, 0.2)', text: '#999' },
  C: { bg: 'rgba(200, 40, 40, 0.2)', text: '#e74c3c' }
};

const statusLabels: Record<string, string> = {
  A: 'Announced',
  R: 'Registering',
  P: 'In Progress',
  F: 'Finished',
  C: 'Cancelled'
};

function getLevelSecondsLeft(tournament: any, now: number): number | null {
  const levelMins = Number(tournament?.levelMins || tournament?.mins || 0);
  if (!Number.isFinite(levelMins) || levelMins <= 0) return null;

  const levelStartedAt = Number(tournament?.levelStartedAt || 0);
  const startAtTime = Number(tournament?.startAtTime || 0);
  const levelStart =
    Number.isFinite(levelStartedAt) && levelStartedAt > 0 ? levelStartedAt : startAtTime;
  if (!Number.isFinite(levelStart) || levelStart <= 0) return null;

  return Math.max(0, Math.ceil(levelStart + levelMins * 60 - now));
}

function formatHeroRuntime(seconds: number): string {
  if (seconds < 60) return `${seconds} SEC${seconds === 1 ? '' : 'S'}`;

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (secs === 0) return `${minutes} MIN${minutes === 1 ? '' : 'S'}`;
  return `${minutes} MIN ${secs} SEC${secs === 1 ? '' : 'S'}`;
}

/**
 * Merge a freshly-pushed server late-registration countdown into the locally
 * ticking value. The display is CLIENT-CLOCK owned: keep the local tick and only
 * re-sync when the server value has genuinely drifted (>2s). The gotTnmStatus
 * feed pushes `lateRegistrationCountDown` ~1×/s and its values round/lag against
 * the 1s tick, so re-seeding on every push made the countdown jump back and
 * forth. Seeds on first landing (`local === null`). Mirrors the break-countdown
 * resync in TournamentScreenModal and the drift-gated sync the in-game
 * Tournament Info dashboard uses. Exported for unit testing.
 */
export function mergeLateRegCountdown(local: number | null, serverValue: number): number {
  if (local === null) return serverValue; // first landing → seed
  if (Math.abs(serverValue - local) > 2) return serverValue; // genuine drift → resync
  return local; // within tolerance → keep the local tick
}

/**
 * Format a (days, hours, mins) tuple into the hero countdown / runtime
 * suffix used by both the "RUNNING FOR …" (elapsed) and "IN …" (until-
 * start) hero variants. Singular forms ("1 HR", "1 MIN") drop the trailing
 * S — Unity does the same, and the leading "1 HRS" looked wrong when it
 * landed on screen for exactly one hour / minute.
 */
function formatHeroCountdownParts(
  days: number,
  hours: number,
  mins: number,
  secs?: number
): string {
  if (days > 0) return `${days}D ${hours} HR${hours === 1 ? '' : 'S'}`;
  if (hours > 0) return `${hours} HR${hours === 1 ? '' : 'S'} ${mins} MIN${mins === 1 ? '' : 'S'}`;
  if (mins > 0) return `${mins} MIN${mins === 1 ? '' : 'S'}`;
  // Under a minute: the "IN …" until-start countdown passes the remaining
  // seconds so it ticks down 59s → 1s (Unity parity); the "RUNNING FOR …"
  // elapsed variant omits secs and keeps the coarse "<1 MIN".
  if (secs !== undefined) return `${secs} SEC${secs === 1 ? '' : 'S'}`;
  return '<1 MIN';
}

/**
 * Format a satellite live-schedule value into "Mon D, YYYY" for the card.
 * The lobby broadcast (findTournamentsToBroadcast → `t.*`) ships the raw
 * `satelliteLiveSchedule` / `satelliteLiveScheduleEnd` columns as epoch
 * SECONDS, so handle numbers (and numeric strings) first. Also accept the
 * engine-formatted "MM/DD/YYYY" string (lobby-list query aliases liveStart /
 * liveEnd) and pass any other string through unchanged.
 */
function formatSatelliteDate(value?: string | number): string {
  if (value === undefined || value === null || value === '') return '';

  // Epoch seconds/ms (raw column) or a purely-numeric string.
  const asNumber =
    typeof value === 'number' ? value : /^\d+$/.test(String(value).trim()) ? Number(value) : NaN;
  if (Number.isFinite(asNumber) && asNumber > 0) {
    const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }

  // "MM/DD/YYYY" → "Mon D, YYYY".
  const str = String(value);
  const parts = str.split('/').map((n) => Number(n));
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n) && n > 0)) {
    const [m, d, y] = parts;
    return new Date(y, m - 1, d).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  return str;
}

const TournamentDetailModal = ({
  open,
  onClose,
  tournament,
  autoRegisterOnOpen,
  onAutoRegisterConsumed,
  rematch
}: TournamentDetailModalProps) => {
  const { openTable, requestTableSlot } = useContext(ViewManagerContext);
  const { openCashier } = useContext(CashierContext);
  const { socket, actionPathStatus, connectionStatus } = useContext(SocketContext);
  const { register, unregister, subscribeTournamentDetails, unsubscribeTournamentDetails } =
    useGameService();
  const dispatch = useAppDispatch();
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const serverTime = useAppSelector((state) => state.serverDetails.serverTime);
  const playerTournamentDetails = useAppSelector((state) => state.lobby.playerTournamentDetails);
  const tournamentList = useAppSelector((state) => state.lobby.tournament.tournamentList);
  const sitAndGoList = useAppSelector((state) => state.lobby.tournament.sitAndGoList);
  // state.playerGames is the SECOND chip source in TableHeader after
  // state.tables. After SNG unregister we have to clear THIS slot too —
  // otherwise the chip stays shown via tablesFromPlayerGames until the
  // next GOT_PLAYER_GAMES from the engine arrives. Ref so the latest
  // value is visible inside the long-lived socket handler closure.
  const playerGames = useAppSelector((state) => state.playerGames);
  const playerGamesRef = useRef(playerGames);
  useEffect(() => {
    playerGamesRef.current = playerGames;
  }, [playerGames]);

  const [confirmAction, setConfirmAction] = useState<'register' | 'unregister' | null>(null);
  const [loading, setLoading] = useState(false);
  const [resultMsg, setResultMsg] = useState('');
  // Server-rejected registration (e.g. insufficient balance, tournament full) —
  // surfaced as a clear failure modal, matching Unity, instead of a green inline
  // note the player can miss. `registerErrorFunds` marks the insufficient-funds
  // case, which additionally offers a Cashier shortcut.
  const [registerError, setRegisterError] = useState('');
  const [registerErrorFunds, setRegisterErrorFunds] = useState(false);
  // Heading for the failure modal — 'Registration Failed!' for a register
  // rejection, 'Re-Entry Failed' for a re-entry (rebuy) rejection.
  const [registerErrorTitle, setRegisterErrorTitle] = useState('Registration Failed!');
  // Opens the full Tournament Info dashboard (ranking / prizes / tables /
  // blinds) — the same panel shown in-table, driven here by props so it can
  // render from the lobby. Replaces the old "Observe" affordance; the felt-
  // observe capability lives inside that dashboard's Tables tab.
  const [showInfo, setShowInfo] = useState(false);

  // EN-11016: a later occurrence of a recurring tournament, projected by the
  // lobby. Display only: it has no live feed of its own and cannot be
  // registered for, so the hero's "Registration starts on …" is its action.
  const isProjected = !!tournament?.isProjected;

  // EN-11109: the next table a rematch moved to after its first one filled up
  // (see handleRematchTableFilled). Null otherwise, and always for a lobby
  // registration, so `tournament` is used as before.
  const [rematchRetarget, setRematchRetarget] = useState<any | null>(null);
  const targetTournament = rematch && rematchRetarget ? rematchRetarget : tournament;

  // Keep tournament data live from Redux
  const liveTournament = useMemo(() => {
    if (!targetTournament) return null;
    if (targetTournament.isProjected) return targetTournament;
    const id = targetTournament.tournamentId || targetTournament.id;
    return (
      [...(tournamentList || []), ...(sitAndGoList || [])].find(
        (t: any) => (t.tournamentId || t.id) === id
      ) || targetTournament
    );
  }, [tournamentList, sitAndGoList, targetTournament]);

  // Player's per-tournament registration record: status + assigned table gameID
  const playerDetail = useMemo(() => {
    if (!liveTournament) return null;
    return (
      (playerTournamentDetails || []).find(
        (t: any) => Number(t.tournamentId) === Number(liveTournament.tournamentId)
      ) || null
    );
  }, [playerTournamentDetails, liveTournament]);

  const myStatus = playerDetail?.status || '';
  const myGameID = playerDetail?.gameID;

  // Late-registration countdown, sourced from the SAME tournament-status feed
  // Tournament Info uses (`lateRegistrationCountDown`, seconds). We subscribe to
  // the tournament while the modal is open (below) so the value flows into
  // state.currentTable.tournamentStatus; here we read the record for this
  // tournament. Guarded for the cold-start / test case where the table slice
  // hasn't hydrated. `undefined` → the row falls back to a plain status word.
  const feedLateRegCountdown = useAppSelector((state) => {
    const records = (state as any).currentTable?.tournamentStatus;
    if (!Array.isArray(records) || !liveTournament) return undefined;
    const rec = records.find(
      (s: any) => Number(s?.tournamentId) === Number(liveTournament.tournamentId)
    );
    return typeof rec?.lateRegistrationCountDown === 'number'
      ? rec.lateRegistrationCountDown
      : undefined;
  });
  // Tick down locally each second; the display is CLIENT-CLOCK owned, not
  // re-derived from the server each push. `null` until the feed lands.
  const [lateRegSecondsLeft, setLateRegSecondsLeft] = useState<number | null>(null);
  useEffect(() => {
    if (typeof feedLateRegCountdown !== 'number') return;
    setLateRegSecondsLeft((local) => mergeLateRegCountdown(local, feedLateRegCountdown));
  }, [feedLateRegCountdown]);
  const hasLateRegCountdown = lateRegSecondsLeft !== null;
  useEffect(() => {
    if (!hasLateRegCountdown) return;
    const id = window.setInterval(
      () => setLateRegSecondsLeft((v) => (v === null ? null : Math.max(0, v - 1))),
      1000
    );
    return () => window.clearInterval(id);
  }, [hasLateRegCountdown]);

  // Set after a register / late-register / re-entry on an IN-PROGRESS tournament,
  // while the engine assigns a seat. Drives the "Finding the best seat for you…"
  // disabled button (Unity parity) so the player sees progress and can't
  // double-submit. Cleared when the seat is found (the shouldAutoRedirect effect
  // navigates → modal closes → reset) or by the safety timeout below.
  const [findingSeat, setFindingSeat] = useState(false);
  const isSitNGo = liveTournament?.gameType === TableType.SitNGo;
  // EN-11306 (release.sit-go-buy-in-lobby): a Sit & Go is named here the way
  // its lobby card names it (EN-11139 AC 5), so the card and this modal never
  // disagree. Off, or for an MTT: the template's own title, as before.
  const sitGoBuyInLobby = useFeatureFlag('sitGoBuyInLobby');
  const tournamentName: string | undefined =
    isSitNGo && sitGoBuyInLobby && liveTournament
      ? sitGoCardName({
          capacity: Number(liveTournament.capacity ?? liveTournament.maxPlayers ?? 0),
          name: liveTournament.tablename || ''
        })
      : liveTournament?.tablename;
  const sngPayoutEnabled = useFeatureFlag('sngPayoutStructure');
  const showSngPayouts = sngPayoutEnabled && (isSitNGo || liveTournament?.tabletype === 't');

  const hasAssignedSeat = myGameID !== undefined && myGameID !== null && Number(myGameID) !== -1;

  // Subscribe to this tournament while the modal is open so the live
  // `lateRegistrationCountDown` flows into state.currentTable.tournamentStatus —
  // the same feed the Tournament Info dashboard reads. myStatus is read through
  // a ref at teardown so a status change doesn't churn the subscription.
  // Subscribe to this tournament's feed while the modal is open (S2 — the
  // tournament-info surface), so the live lateRegistrationCountDown flows into
  // state.currentTable.tournamentStatus. Balanced: unsubscribe on close/unmount.
  // If the player is registered / has a table open, the App-level centralized
  // effect holds its OWN ref-count on this tournament, so decrementing here on
  // close never actually tears the feed down for them (ref-count + 30s defer).
  useEffect(() => {
    if (!socket || !open || !liveTournament || isProjected) return;
    const tid = liveTournament.tournamentId;
    subscribeTournamentDetails(tid);
    return () => {
      unsubscribeTournamentDetails(tid);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, open, liveTournament?.tournamentId, isProjected]);

  const payouts = useSngPayouts(Number(liveTournament?.tournamentId), open && showSngPayouts);

  // Registration eligibility
  // Late registration: tournament is in progress AND has late entry AND current level is still within the window.
  // Coerce `lateEntry` to boolean with `!!` — it ships as a numeric flag (0/1 or a duration), and
  // the unguarded `&&` chain otherwise propagates a numeric `0` through `hasLateEntry → lateEntryExpired →
  // isLateRegister → showRegister`, which then renders as a literal "0" next to the Observe button in JSX.
  const hasLateEntry = !!liveTournament?.lateEntry && liveTournament?.status === 'P';
  const lateEntryExpired =
    hasLateEntry && !(liveTournament?.lateEntryExpireMins >= liveTournament?.currentLevel);
  const isLateRegister = hasLateEntry && !lateEntryExpired;

  // Register button label and disabled state — mirrors old Tournament.tsx logic
  // exactly. Registration (SNG + MTT) is disabled during maintenance; the player
  // may still open this modal to view the details, but not sign up.
  const { registerLabel, disableRegister, isReentry } = useMemo(() => {
    if (!liveTournament)
      return { registerLabel: 'Register', disableRegister: true, isReentry: false };

    const multiFlightBlock = liveTournament.isMultiFlight && liveTournament.isFinalMultiFlight;
    const qualifiedBlock =
      liveTournament.multiBagOption === 'none' && liveTournament.isQualified === 1;

    if (hasLateEntry) {
      if (myStatus === 'F' && liveTournament.status === 'P') {
        // Re-entry: busted player can re-enter if late entry window is still open
        return {
          registerLabel: 'Re-entry',
          disableRegister: lateEntryExpired || isMaintenance || multiFlightBlock || qualifiedBlock,
          isReentry: true
        };
      }
      // Late entry path: enabled unless level window expired or other blocks.
      // Late registration pays the buy-in, not the re-entry fee.
      return {
        registerLabel: 'Late Register',
        disableRegister: lateEntryExpired || multiFlightBlock || isMaintenance || qualifiedBlock,
        isReentry: false
      };
    }

    // Normal path: only enabled when status is 'R' (Registering)
    return {
      registerLabel: 'Register',
      disableRegister:
        liveTournament.status !== 'R' || isMaintenance || multiFlightBlock || qualifiedBlock,
      isReentry: false
    };
  }, [liveTournament, myStatus, serverTime, isMaintenance, hasLateEntry, lateEntryExpired]);

  // Guests can't register — the CTA stays clickable and routes to sign in /
  // sign up. SNGs read "…to Join" (you join a sit-n-go); MTTs read "…Register".
  const guest = useGuest();
  const guestRegisterLabel = isSitNGo ? GUEST_CTA.sng : GUEST_CTA.mtt;

  // Join table: player is seated (P), or registered (R) in a playing tournament
  // with a seat assigned, or registered in a SnG.
  // Mirrors Unity's LobbiesController gate: IsSubscribed + tournament.Status == PLAYING.
  // Is the tournament in-progress? Prefer the FAST gotPlayerTournamentDetails
  // feed (tStatus) over liveTournament.status: the lobby tournament list LAGS,
  // so after a tournament actually starts it can still read 'R' for a while,
  // leaving the button stuck on "Unregister" (reported). The fast feed's tStatus
  // flips to 'P' promptly. `myStatus === 'P'` (the player's own status) is the
  // strongest signal — they're seated and playing — and is handled below.
  const tournamentIsPlaying = playerDetail?.tStatus === 'P' || liveTournament?.status === 'P';

  const showJoinTable =
    (myStatus === 'P' &&
      liveTournament?.closed === 0 &&
      liveTournament?.status !== 'C' &&
      liveTournament?.isMultiFlightBreak !== 1) ||
    (myStatus === 'R' &&
      liveTournament?.gameType === TableType.SitNGo &&
      liveTournament?.closed === 0 &&
      liveTournament?.status !== 'C') ||
    (myStatus === 'R' &&
      tournamentIsPlaying &&
      liveTournament?.closed === 0 &&
      liveTournament?.isMultiFlightBreak !== 1 &&
      hasAssignedSeat) ||
    // Additional prompt trigger off the fast gotPlayerTournamentDetails feed:
    // liveTournament comes from the lobby tournament list, which LAGS — after a
    // tournament starts and seats the player, the lobby list can still read 'R'
    // for a while, leaving the button stuck on "Unregister" when the player taps
    // "Go to Tournament". The player-broadcast feed arrives ~1/s and its
    // server-computed shouldAutoRedirect is exactly "registered + tournament
    // PLAYING + seat assigned + not maintenance/closed/multi-flight-break" — the
    // precise "should be at the table now" signal that flips the button promptly.
    // (It stays false pre-start, so a still-Registering tournament hides Join.)
    (myStatus === 'R' &&
      playerDetail?.shouldAutoRedirect === true &&
      liveTournament?.closed === 0 &&
      liveTournament?.status !== 'C');

  // Unregister: player is registered and the tournament hasn't started for them
  // yet. Once the fast feed says they should be at the table (shouldAutoRedirect),
  // you can't unregister — the button flips to Join Table (above) instead of
  // staying on a stale "Unregister".
  const showUnregister =
    myStatus === 'R' &&
    liveTournament?.status === 'R' &&
    // Flip off the moment the FAST feed says the tournament is in-progress —
    // don't wait for the lagging lobby list or shouldAutoRedirect. Otherwise the
    // button stays on a stale "Unregister" after the tournament has started.
    !tournamentIsPlaying &&
    !playerDetail?.shouldAutoRedirect;

  // Show register button: not already seated AND (tournament is registering OR late entry available)
  // Also show for re-entry: player busted (myStatus 'F') and tournament has lateEntry flag.
  // A FREEZEOUT never allows re-entry, so a busted player (myStatus 'F') gets no
  // register button — but a not-yet-entered player can still late-register.
  const isFreezeout = isFreezeoutGameType(liveTournament?.gameType);
  const isFreezeoutReentry = isFreezeout && myStatus === 'F' && liveTournament?.status === 'P';
  const showRegister =
    !isProjected &&
    !showJoinTable &&
    !showUnregister &&
    myStatus !== 'P' &&
    !isFreezeoutReentry &&
    (liveTournament?.status === 'R' ||
      isLateRegister ||
      (myStatus === 'F' && liveTournament?.status === 'P' && isLateRegister));

  // Auto-redirect: the broadcast computes shouldAutoRedirect server-side
  // (registered + tournament in progress + seat assigned + not in maintenance/break/closed).
  // The client just reads the flag — no conditions evaluated here.
  //
  // The ref guard ensures we only redirect once per "open" cycle — if the
  // user backs out of the table and re-opens the modal, the effect should
  // fire again, but a Redux update while the modal is open shouldn't
  // trigger a second redirect.
  const autoRedirectedRef = useRef(false);
  // A rematch's second try at opening its table (openRematchTable). Held
  // outside the listener effect: the registration's own lobby push re-runs that
  // effect, which must not cancel the retry.
  const rematchOpenRetryRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(rematchOpenRetryRef.current), []);
  // Set when this dialog's own Confirm sends a rematch registration
  // (handleRegister). Only then may it open the next table and retire the
  // finished one.
  const rematchRegisterSentRef = useRef(false);
  // EN-11109: a rematch registration is serialized per next tournament across
  // every table host (rematchRegistrationsInFlight). This host's identity
  // there, the tournament it claimed, the wait it joined when another host
  // had claimed it, and whether its rematch already completed (later replies
  // for that tournament, such as an "already registered" rejection, are then
  // ignored).
  const rematchHostRef = useRef({});
  const rematchClaimRef = useRef<string | null>(null);
  const rematchWaitRef = useRef<(() => void) | null>(null);
  const rematchDoneRef = useRef(false);
  // Set when this dialog's own rematch registration was accepted.
  const rematchRegisteredRef = useRef(false);
  const rematchRef = useRef(rematch);
  rematchRef.current = rematch;
  const settleOwnRematch = useCallback(
    (outcome: Parameters<typeof settleRematchRegistration>[2]) => {
      const claimed = rematchClaimRef.current;
      if (!claimed) return;
      rematchClaimRef.current = null;
      settleRematchRegistration(claimed, rematchHostRef.current, outcome);
    },
    []
  );
  // Registered at the next tournament, whose table id has not arrived yet
  // (gameID missing or -1 in both the lobby row and the player's details):
  // the dialog shows "Opening your table" and opens it once the id arrives.
  const [rematchAwaitingTable, setRematchAwaitingTableState] = useState(false);
  const rematchAwaitingTableRef = useRef(false);
  const setRematchAwaitingTable = useCallback((awaiting: boolean) => {
    rematchAwaitingTableRef.current = awaiting;
    setRematchAwaitingTableState(awaiting);
  }, []);
  const rematchOpened = useCallback(() => {
    rematchDoneRef.current = true;
    setRematchAwaitingTable(false);
    rematchRef.current?.onRegistered();
    settleOwnRematch({ kind: 'opened' });
  }, [settleOwnRematch, setRematchAwaitingTable]);
  const rematchRefused = useCallback(
    () => settleOwnRematch({ kind: 'refused' }),
    [settleOwnRematch]
  );
  // The owner's watch on its own claim: it hears only the claim timing out
  // (a lost reply), since every other outcome is its own.
  const rematchOwnWatchRef = useRef<(() => void) | null>(null);
  // Set when a rematch registration's outcome is unknown and the player is
  // not registered: Confirm Registration is shown again with a retry line.
  const [rematchRetry, setRematchRetry] = useState(false);
  // The latest registration state, for outcomes that arrive after the render
  // that set them up.
  const rematchLatestRef = useRef({ myStatus, myGameID, liveTournament, openTable });
  rematchLatestRef.current = { myStatus, myGameID, liveTournament, openTable };
  /**
   * Whether the player is registered at the rematch's next tournament
   * (playerTournamentDetails status 'R' or 'P'), sending nothing either way:
   *  - 'opened': registered, and the table's id is known. Its open has
   *    started, in place of the finished table, which is retired once it opens.
   *  - 'awaiting-table': registered, but no valid id yet. The dialog waits,
   *    visibly, and opens the table once one arrives (see the effect below).
   *  - 'not-registered': the caller sends, or offers Confirm again.
   */
  const completeRematchIfRegistered = useCallback(():
    | 'opened'
    | 'awaiting-table'
    | 'not-registered' => {
    const current = rematchRef.current;
    const latest = rematchLatestRef.current;
    const registered = latest.myStatus === 'R' || latest.myStatus === 'P';
    if (!current || !latest.liveTournament || !registered) {
      setRematchAwaitingTable(false);
      return 'not-registered';
    }
    const nextGameID =
      Number(latest.liveTournament.gameID) > 0 ? latest.liveTournament.gameID : latest.myGameID;
    if (!(Number(nextGameID) > 0)) {
      setRematchAwaitingTable(true);
      return 'awaiting-table';
    }
    setRematchAwaitingTable(false);
    const game = latest.liveTournament.game || 'texas';
    openRematchTable(
      () =>
        rematchLatestRef.current.openTable(String(nextGameID), game, 'no', {
          leavingGameID: current.leavingGameID
        }),
      rematchOpened,
      rematchOpenRetryRef
    );
    return 'opened';
  }, [rematchOpened, setRematchAwaitingTable]);
  /**
   * A rematch registration whose outcome is unknown (its owner went away, its
   * reply never came) or whose table could not open. Reconcile first: if the
   * player is registered, complete as a success, or wait for the table's id.
   * Otherwise return to Confirm Registration with a retry line. Nothing is
   * sent until the player taps Confirm again.
   */
  const recoverRematch = useCallback(() => {
    setLoading(false);
    setFindingSeat(false);
    if (completeRematchIfRegistered() !== 'not-registered') return;
    setRematchRetry(true);
    setConfirmAction('register');
  }, [completeRematchIfRegistered]);
  // While waiting for the table's id: open it the moment the lobby row or the
  // player's details carry one. If the registration is gone meanwhile, offer
  // Confirm again.
  useEffect(() => {
    if (!rematchAwaitingTable || rematchDoneRef.current) return;
    if (completeRematchIfRegistered() !== 'not-registered') return;
    setRematchRetry(true);
    setConfirmAction('register');
  }, [
    rematchAwaitingTable,
    liveTournament?.gameID,
    myGameID,
    myStatus,
    completeRematchIfRegistered
  ]);
  // Cancel (or the X) while waiting for the table: back to the offer. This
  // dialog's rematch is over, so a table id, a seat or a reply arriving later
  // changes nothing.
  const cancelRematchAwaitingTable = useCallback(() => {
    rematchDoneRef.current = true;
    setRematchAwaitingTable(false);
    rematchRef.current?.onCancel();
  }, [setRematchAwaitingTable]);
  // The Registration Failed screen, for this dialog's own rejection or for the
  // one it waited on.
  const showRegisterFailure = useCallback((failure: RematchRegistrationFailure) => {
    setRegisterError(failure.message);
    setRegisterErrorFunds(failure.insufficientFunds);
    setRegisterErrorTitle(failure.title || 'Registration Failed!');
    setResultMsg('');
    // Clear any transient "Finding the best seat…" / processing state from a
    // late-register attempt so closing the failure returns to a clean view.
    setFindingSeat(false);
    setLoading(false);
  }, []);
  // EN-11109: the tournaments whose registration was refused because their
  // table filled up, how many moves to another table were used, when the
  // search for one gives up, whether it is waiting for a table to be listed,
  // whether it gave up, and a move waiting to send its registration once the
  // dialog has re-rendered on the new table (see the effect after
  // handleRegister).
  const rematchFilledIdsRef = useRef<string[]>([]);
  const rematchMovesRef = useRef(0);
  const rematchSearchDeadlineRef = useRef<number | null>(null);
  const [rematchSearching, setRematchSearching] = useState(false);
  const [rematchNoOpenTable, setRematchNoOpenTable] = useState(false);
  const [rematchMoveSend, setRematchMoveSend] = useState<string | null>(null);
  /**
   * Move the rematch on to the template's next open table, or wait for one to
   * be listed, or give up: after REMATCH_MAX_MOVES moves or REMATCH_SEARCH_MS
   * since the first table filled up, the No open table notice.
   */
  const moveRematchOn = useCallback(() => {
    const deadline = rematchSearchDeadlineRef.current ?? 0;
    if (rematchMovesRef.current >= REMATCH_MAX_MOVES || Date.now() >= deadline) {
      setRematchSearching(false);
      setLoading(false);
      setRematchNoOpenTable(true);
      return;
    }
    const next = rematchRef.current?.findNextTable?.(rematchFilledIdsRef.current) ?? null;
    const nextId = next ? String(next.tournamentId ?? next.id ?? '') : '';
    if (!next || !nextId || rematchFilledIdsRef.current.includes(nextId)) {
      setRematchSearching(true);
      return;
    }
    rematchMovesRef.current += 1;
    setRematchSearching(false);
    // Stays loading: the dialog moves to the next table, then sends.
    setRematchRetarget(next);
    setRematchMoveSend(nextId);
  }, []);
  /**
   * The engine refused this dialog's rematch registration because the table
   * filled up first (another Play Again took the last seat). Nothing was
   * charged (the refusal precedes any debit), so the player, who already
   * confirmed this buy-in, is moved on to the template's next open table and
   * registered there without another Confirm (moveRematchOn).
   */
  const handleRematchTableFilled = useCallback(
    (refusedTournamentId: string) => {
      if (refusedTournamentId && !rematchFilledIdsRef.current.includes(refusedTournamentId)) {
        rematchFilledIdsRef.current = [...rematchFilledIdsRef.current, refusedTournamentId];
      }
      // Hosts waiting on this tournament's registration learn it moved on.
      settleOwnRematch({ kind: 'moved' });
      if (rematchSearchDeadlineRef.current === null) {
        rematchSearchDeadlineRef.current = Date.now() + REMATCH_SEARCH_MS;
      }
      moveRematchOn();
    },
    [settleOwnRematch, moveRematchOn]
  );
  // While no next table is listed, re-read the lobby list until one is, or
  // until the search gives up.
  useEffect(() => {
    if (!rematchSearching) return undefined;
    const timer = window.setInterval(moveRematchOn, REMATCH_SEARCH_POLL_MS);
    return () => window.clearInterval(timer);
  }, [rematchSearching, moveRematchOn]);
  // Cancel (or the X) while finding a table, or on the No open table notice:
  // back to the offer. Nothing was registered.
  const cancelRematchSearch = useCallback(() => {
    rematchDoneRef.current = true;
    setRematchSearching(false);
    setRematchNoOpenTable(false);
    setLoading(false);
    rematchRef.current?.onCancel();
  }, []);
  // Closing or unmounting gives up the claim and the wait, so neither outlives
  // this host.
  useEffect(() => {
    if (!open) return undefined;
    const host = rematchHostRef.current;
    return () => {
      rematchOwnWatchRef.current?.();
      rematchOwnWatchRef.current = null;
      if (rematchClaimRef.current) releaseRematchRegistration(rematchClaimRef.current, host);
      rematchClaimRef.current = null;
      rematchWaitRef.current?.();
      rematchWaitRef.current = null;
      rematchDoneRef.current = false;
      rematchRegisteredRef.current = false;
      rematchFilledIdsRef.current = [];
      rematchMovesRef.current = 0;
      rematchSearchDeadlineRef.current = null;
      setRematchSearching(false);
      setRematchNoOpenTable(false);
      setRematchMoveSend(null);
      setRematchRetarget(null);
      setRematchRetry(false);
      setRematchAwaitingTable(false);
    };
  }, [open, setRematchAwaitingTable]);
  useEffect(() => {
    if (!open) {
      autoRedirectedRef.current = false;
      rematchRegisterSentRef.current = false;
      return;
    }
    if (autoRedirectedRef.current) return;
    if (!liveTournament || !playerDetail) return;
    if (!playerDetail.shouldAutoRedirect) return;
    // A rematch dialog acts only on a seat its own Confirm asked for: another
    // finished table's offer can target the same next table, and the seat its
    // Confirm took must not retire this one. A dialog that waits on another
    // host's registration follows that host's outcome instead.
    // A dialog waiting for its table's id (registered, nothing sent) confirmed
    // too, so the seat the server assigns at start opens its table.
    const confirmedHere = rematchRegisterSentRef.current || rematchAwaitingTableRef.current;
    if (rematch && (!confirmedHere || rematchDoneRef.current)) return;

    autoRedirectedRef.current = true;
    const isSng = liveTournament.gameType === TableType.SitNGo;
    const targetGameID = isSng ? liveTournament.gameID : myGameID;
    if (!targetGameID || Number(targetGameID) === -1) return;
    const targetGame = liveTournament.game || 'texas';
    // EN-11109 rematch: a seat already assigned means the registration took,
    // even if its reply has not arrived (and once this host closes, nothing
    // would hear it). Open the next table in place of the finished one and
    // retire that one, as the reply would have, trying once more past a table
    // switch's transition. If that is refused too, the host closes as before;
    // the finished table is kept.
    if (rematch) {
      openRematchTable(
        () =>
          openTable(String(targetGameID), targetGame, 'no', {
            leavingGameID: rematch.leavingGameID
          }),
        rematchOpened,
        rematchOpenRetryRef,
        () => {
          rematchRefused();
          onClose();
        }
      );
      return;
    }
    onClose();
    openTable(String(targetGameID), targetGame, 'no');
  }, [
    open,
    liveTournament,
    playerDetail,
    myGameID,
    onClose,
    openTable,
    rematch,
    rematchOpened,
    rematchRefused
  ]);

  // Listen for register/unregister responses — show inline feedback
  useEffect(() => {
    if (!socket || !open) return;

    const handleRegResult = (raw: any) => {
      const data = decompressData(raw);
      if (!data) return;
      // EN-11109: every finished table hosts its own rematch Confirm, so a grid
      // can have two listening. A rematch dialog acts only on the reply to its
      // own registration: it must have sent one (two finished tables' offers
      // can target the same next table, and the grid cell and the panel of one
      // table both host a Confirm), and the engine echoes the request's
      // tournamentId, which must be this dialog's. Once its rematch has
      // completed, a later reply for the same tournament (an "already
      // registered" rejection) changes nothing.
      if (rematch && (!rematchRegisterSentRef.current || rematchDoneRef.current)) return;
      const replyTournamentId = data.tournamentId;
      if (
        rematch &&
        replyTournamentId !== undefined &&
        replyTournamentId !== null &&
        String(replyTournamentId) !== String(liveTournament?.tournamentId ?? liveTournament?.id)
      ) {
        return;
      }
      // A rematch stays locked (loading) through a success until its table has
      // opened or been refused, so Register cannot come back meanwhile. Every
      // other outcome below unlocks it.
      if (!rematch) setLoading(false);
      const msg = data?.response?.msg || data?.msg || '';
      const title = data?.response?.title || '';
      // The engine titles every rejection ('Registration Failed' for
      // insufficient balance / full / expired, or 'Registration unavailable');
      // success is 'Registration Successful'. Route an explicit non-success
      // title to a clear failure modal instead of a green success-styled note.
      const isFailure = !!title && title !== 'Registration Successful';

      // EN-11109: the table filled up before this rematch's registration
      // reached it. A lobby registration keeps the engine's message below.
      if (rematch && isFailure && isRegistrationClosedRefusal(title, msg)) {
        handleRematchTableFilled(
          String(replyTournamentId ?? liveTournament?.tournamentId ?? liveTournament?.id ?? '')
        );
        return;
      }

      // EN-11109: a rematch told it is already registered took a seat with an
      // earlier Confirm (its reply was lost and the player's details had not
      // caught up). The player is registered and charged, so it completes as a
      // success below, never as a failure. A lobby registration keeps the
      // engine's message.
      const alreadyRegistered = !!rematch && isFailure && isAlreadyRegisteredReply(title, msg);

      if (isFailure && !alreadyRegistered) {
        // For the insufficient-funds rejection specifically, rewrite the
        // engine's generic copy to name the minimum buy-in (the amount the
        // player is short), matching Unity. Other failures (e.g. tournament
        // full) keep their message and get a plain Close (no Cashier).
        const isInsufficient = /enough\s+(chips|balance|funds)/i.test(msg);
        const failure: RematchRegistrationFailure = {
          message: isInsufficient
            ? `You do not have enough balance to buy the ticket of the tournament. Minimum buy-in is ${currencyFormatter(
                liveTournament?.buyIn || 0
              )}`
            : msg || 'Registration failed. Please try again.',
          insufficientFunds: isInsufficient
        };
        showRegisterFailure(failure);
        // Every rematch dialog waiting on this registration shows it too.
        if (rematch) settleOwnRematch({ kind: 'failed', failure });
        return;
      }

      // This dialog's rematch registration took: it never sends another.
      if (rematch) rematchRegisteredRef.current = true;
      if (msg) setResultMsg(msg);

      // Registered into a tournament that has ALREADY reached its start time but
      // is still in Registering (not enough players) — surface the "waiting for
      // players" notice immediately, rather than waiting for the per-player feed
      // to flip myStatus to 'R'. Uses Date.now() (not the closure's stale `now`)
      // since this handler is long-lived. Not-yet-started registrations skip it
      // (the time-based effect opens the notice when the clock crosses start).
      const startReached =
        Number(liveTournament?.startAtTime) > 0 &&
        Math.floor(Date.now() / 1000) >= Number(liveTournament?.startAtTime);
      if (liveTournament?.status === 'R' && startReached) {
        waitingNoticeDismissedRef.current = false;
        setShowWaitingModal(true);
      }

      // For SNG auto-join, open the table — but ONLY once the engine has
      // actually assigned a real table. A not-yet-startable SNG (e.g. still
      // waiting for enough players) replies with gameID -1; navigating there
      // lands on the dead /table/-1 felt, so we ignore the sentinel and just
      // surface the server message (set above).
      // EN-11109 rematch: open the next table even when the reply carries the
      // not-yet-startable -1 (a Sit & Go IS its table, so the row has its own
      // gameID), naming the finished table as the seat being left so it does
      // not count toward the table cap — then retire it. The already-registered
      // reply echoes the request, so it carries no isSitAndGo.
      if (rematch && (data?.response?.isSitAndGo || alreadyRegistered)) {
        const replyGameID = Number(data?.gameID) > 0 ? data.gameID : null;
        const rowGameID = Number(liveTournament?.gameID) > 0 ? liveTournament.gameID : null;
        const nextGameID = replyGameID ?? rowGameID;
        if (nextGameID) {
          // The player is registered. A cap or maintenance refusal shows its
          // own notice; the finished table is kept and the registered one
          // holds its slot in Current Tables.
          openRematchTable(
            () =>
              openTable(String(nextGameID), data.game || liveTournament?.game || 'texas', 'no', {
                leavingGameID: rematch.leavingGameID
              }),
            rematchOpened,
            rematchOpenRetryRef,
            () => {
              setLoading(false);
              rematchRefused();
            }
          );
          return;
        }
        // Registered, but no table to open: the finished table is kept.
        rematchRefused();
      }
      if (rematch) setLoading(false);

      if (data?.response?.isSitAndGo && Number(data?.gameID) > 0) {
        onClose();
        openTable(String(data.gameID), data.game || 'texas');
      }
    };

    const handleUnregResult = (raw: any) => {
      const data = decompressData(raw);
      if (!data) return;
      setLoading(false);
      const msg = data?.response?.msg || data?.msg || '';
      if (msg) setResultMsg(msg);

      // SNG cleanup: when register succeeded the handleRegResult above
      // auto-opened the SNG table (state.tables[gameID] populated, header
      // chip shown). The engine doesn't push a follow-up UNSUBSCRIBE on
      // unregister, so without this the chip lingers and state.tables
      // keeps an orphan slot. MTT unregister has no assigned table to
      // clean — the seat is allocated at start time, not at register —
      // so this is a no-op there.
      // The engine emits the same 'gotTnmUnRegister' event for both
      // success and recoverable failures (already-assigned, already-
      // unregistered, late-window). Gate on the success title so a
      // server-side rejection doesn't strip a still-valid registration
      // off the client.
      const title = data?.response?.title || '';
      const isSuccess = title === 'Unregister Successful';
      const isSng = liveTournament?.gameType === TableType.SitNGo;
      const sngGameID = liveTournament?.gameID;
      if (
        isSuccess &&
        isSng &&
        sngGameID !== undefined &&
        sngGameID !== null &&
        Number(sngGameID) > 0
      ) {
        const gidStr = String(sngGameID);
        // Mark BEFORE emit — App.tsx's GOT_TEXAS / GOT_OMAHA queue drops
        // any in-flight broadcast for this gid during the 30s grace
        // window. Without this, a broadcast already in transit (lambda
        // and engine processing the unsubscribe asynchronously) would
        // re-populate state.tables and the chip would flicker back on
        // the next TableHeader render even after removeTable cleared it.
        // Also blocks Lobby.tsx's derive-from-seat auto-subscribe from
        // re-emitting if the engine's lobby cache lags behind unregister.
        markRecentlyExitedTable(gidStr);
        if (socket) {
          socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, {
            gameID: gidStr,
            game: liveTournament?.game || 'texas'
          });
        }
        dispatch(removeTable(gidStr));
        // Drop the SNG from state.playerGames too — TableHeader's chip list
        // is union(state.tables, state.playerGames). Without this, the SNG
        // chip stays visible from the playerGames source even though
        // state.tables[gid] is gone. The engine will eventually push a
        // refreshed got_playerGames without this entry, but that round-trip
        // is what was leaving the stale chip on screen.
        const filtered = (playerGamesRef.current || []).filter(
          (g: any) => g && String(g.gameID) !== gidStr
        );
        dispatch(updatePlayerGames(filtered));
      }
    };

    // Re-entry (rebuy) rejections. A lobby re-entry emits action 'register',
    // but the engine routes a busted player to the 'rebuy' flow, which replies
    // on GOT_TOURNAMENT_REBUY — NOT gotTnmRegister. Its failure payload is
    // { error: true, msg, title: 'Re-Entry Failed' } (top-level, not nested in
    // `response`). Without this listener an over-limit re-entry got no feedback.
    const handleRebuyResult = (raw: any) => {
      const data = decompressData(raw);
      if (!data) return;
      const msg = data?.response?.msg || data?.msg || '';
      const title = data?.response?.title || data?.title || '';
      const isFailure =
        data?.error === true || data?.response?.error === true || /fail/i.test(title);
      if (!isFailure) return; // success seats the player via the normal flow
      const isInsufficient = /enough\s+(chips|balance|funds)/i.test(msg);
      setRegisterError(msg || 'Re-entry failed. Please try again.');
      setRegisterErrorFunds(isInsufficient);
      setRegisterErrorTitle('Re-Entry Failed');
      setResultMsg('');
      setFindingSeat(false);
      setLoading(false);
    };

    socket.on(GOT_TOURNAMENT_REGISTER, handleRegResult);
    socket.on(GOT_TOURNAMENT_UNREGISTER, handleUnregResult);
    socket.on(GOT_TOURNAMENT_REBUY, handleRebuyResult);
    return () => {
      socket.off(GOT_TOURNAMENT_REGISTER, handleRegResult);
      socket.off(GOT_TOURNAMENT_UNREGISTER, handleUnregResult);
      socket.off(GOT_TOURNAMENT_REBUY, handleRebuyResult);
    };
  }, [
    socket,
    open,
    onClose,
    openTable,
    dispatch,
    liveTournament,
    rematch,
    rematchOpened,
    rematchRefused,
    settleOwnRematch,
    showRegisterFailure,
    handleRematchTableFilled
  ]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setConfirmAction(null);
      setLoading(false);
      setResultMsg('');
      setRegisterError('');
      setRegisterErrorFunds(false);
      setRegisterErrorTitle('Registration Failed!');
      setShowInfo(false);
      setFindingSeat(false);
      setLateRegSecondsLeft(null);
      setShowWaitingModal(false);
      waitingNoticeDismissedRef.current = false;
    }
  }, [open]);

  // Safety net for the "Finding the best seat for you…" state: normally the
  // engine seats the player and navigates (the shouldAutoRedirect effect's
  // openTable closes this modal, and the reset above clears it). If that never
  // happens, don't strand the button disabled — give up after a few seconds.
  useEffect(() => {
    if (!findingSeat) return;
    const id = window.setTimeout(() => setFindingSeat(false), 12000);
    return () => window.clearTimeout(id);
  }, [findingSeat]);

  // Tick every 1s while open so the hero countdown shows a live seconds
  // countdown under a minute (Unity parity: "IN 51 SECS"). It's a single,
  // short-lived modal — a 1s re-render here is cheap (unlike the lobby tiles).
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!open) return;
    setNow(Math.floor(Date.now() / 1000));
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // "Waiting for more players" notice: a registered player whose tournament has
  // reached its scheduled start time but is still in Registering — i.e. not
  // enough sign-ups to kick off (the hero shows "STARTING ASAP"). Without this
  // the player has no idea why the game hasn't started. Auto-opens once per
  // open cycle; the dismissed guard (reset on close) keeps it from re-popping
  // every tick after Close.
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const waitingNoticeDismissedRef = useRef(false);
  const startTimeReached =
    Number(liveTournament?.startAtTime) > 0 && now >= Number(liveTournament?.startAtTime);
  const isWaitingForPlayers =
    open &&
    !isSitNGo &&
    liveTournament?.tabletype !== 't' &&
    myStatus === 'R' &&
    liveTournament?.status === 'R' &&
    startTimeReached;
  useEffect(() => {
    if (isWaitingForPlayers && !waitingNoticeDismissedRef.current) {
      setShowWaitingModal(true);
    }
  }, [isWaitingForPlayers]);
  const dismissWaitingModal = () => {
    setShowWaitingModal(false);
    waitingNoticeDismissedRef.current = true;
  };

  /**
   * EN-11109: a rematch Confirm sends a registration only if no other table's
   * rematch Confirm already did for this tournament. Returns true when this
   * dialog should send it. Otherwise it follows that registration's outcome,
   * or, if the player is already registered there, completes at once.
   */
  const claimRematchConfirm = useCallback((): boolean => {
    if (!rematch || !liveTournament) return true;
    setRematchRetry(false);
    const tournamentId = String(liveTournament.tournamentId ?? liveTournament.id);
    // Registered already (another finished table's rematch took the seat):
    // nothing to send. Open the table and retire this finished one.
    // Or registered but still waiting for its table's id: "Opening your
    // table" until it arrives.
    if (completeRematchIfRegistered() !== 'not-registered') return false;
    // This dialog's own registration is still in flight, or already took and
    // its table is opening (the details feed can lag the reply): never send a
    // second one.
    if (rematchRegisteredRef.current || rematchClaimRef.current === tournamentId) return false;
    if (claimRematchRegistration(tournamentId, rematchHostRef.current)) {
      rematchClaimRef.current = tournamentId;
      // A reply that never comes times the claim out: reconcile then.
      rematchOwnWatchRef.current?.();
      rematchOwnWatchRef.current = awaitRematchRegistration(tournamentId, (outcome) => {
        rematchOwnWatchRef.current = null;
        if (outcome.kind !== 'abandoned') return;
        rematchClaimRef.current = null;
        recoverRematch();
      });
      return true;
    }
    setLoading(true);
    rematchWaitRef.current?.();
    rematchWaitRef.current = awaitRematchRegistration(tournamentId, (outcome) => {
      rematchWaitRef.current = null;
      setLoading(false);
      if (outcome.kind === 'opened') {
        // The other dialog has opened the next table: retire this one too.
        rematchDoneRef.current = true;
        rematchRef.current?.onRegistered();
      } else if (outcome.kind === 'failed') {
        showRegisterFailure(outcome.failure);
      } else if (outcome.kind === 'moved') {
        // The other dialog took the registration on to another table: this
        // one goes back to its offer, with nothing registered here.
        rematchRef.current?.onCancel();
      } else {
        // abandoned or refused: the outcome for this table is not known.
        recoverRematch();
      }
    });
    return false;
  }, [rematch, liveTournament, completeRematchIfRegistered, recoverRematch, showRegisterFailure]);

  const handleRegister = useCallback(() => {
    if (!liveTournament) return;
    // EN-11307: with the action socket down the registration would be dropped:
    // a rematch then sat on "Processing..." until its claim timed out, and the
    // lobby's Register, Late Register or re-entry for 3 s, with nothing said.
    // Say so instead, sending and claiming nothing. Close returns to the offer
    // (a rematch) or to this detail view (the lobby). It also counts a dead
    // socket not yet noticed (isBuyInConnectionLost: the iOS case).
    if (isBuyInConnectionLost(actionPathStatus, connectionStatus)) {
      setConfirmAction(null);
      showRegisterFailure(CONNECTION_LOST_FAILURE);
      return;
    }
    if (!claimRematchConfirm()) {
      setConfirmAction(null);
      return;
    }
    setLoading(true);
    if (rematch) rematchRegisterSentRef.current = true;
    register(liveTournament.tournamentId, liveTournament.gameID);
    setConfirmAction(null);
    // An in-progress tournament seats you immediately — show "Finding the best
    // seat for you…" until the engine assigns a seat and navigates (which closes
    // this modal). A not-yet-started registration just queues you, so no seat
    // search there.
    if (liveTournament.status === 'P') setFindingSeat(true);
    // A rematch stays loading until its outcome, or until its claim times out
    // and is reconciled (recoverRematch): Register must not come back for a
    // buy-in that may have committed.
    if (!rematch) setTimeout(() => setLoading(false), 3000);
  }, [
    liveTournament,
    register,
    rematch,
    claimRematchConfirm,
    actionPathStatus,
    connectionStatus,
    showRegisterFailure
  ]);

  // EN-11109: a rematch moved to the next table (handleRematchTableFilled)
  // sends its one registration there once this dialog shows that table, so the
  // claim, the reply filter and the retire-on-success all apply to it.
  useEffect(() => {
    if (!rematchMoveSend || !liveTournament) return;
    if (String(liveTournament.tournamentId ?? liveTournament.id) !== rematchMoveSend) return;
    setRematchMoveSend(null);
    handleRegister();
  }, [rematchMoveSend, liveTournament, handleRegister]);

  // Register-button click handler. Runs the SnG cap check BEFORE opening the
  // confirmation dialog — at-cap users see the "Reached Table Limit" modal
  // instead of a confirm-then-block flow. MTT bypasses the cap because MTT
  // seats only consume a slot at JOIN time (openTable enforces it then).
  const handleClickRegister = useCallback(() => {
    // Guests observe only — send them straight to the sign in / sign up page.
    if (guest) {
      if (liveTournament) {
        markTournamentIntentBeforeLogin(String(liveTournament.tournamentId ?? liveTournament.id));
      }
      redirectToLogin();
      return;
    }
    if (disableRegister || loading || findingSeat) return;
    const isSng = liveTournament?.gameType === TableType.SitNGo;
    // A rematch replaces the finished table it was offered on, so it takes no
    // new slot; openTable's leavingGameID accounts for it on success.
    if (isSng && !rematch && !requestTableSlot()) return;
    setConfirmAction('register');
  }, [guest, disableRegister, loading, findingSeat, liveTournament, requestTableSlot, rematch]);

  // SaloonLobbyContainer re-opens this modal for the tournament a guest was
  // trying to register for once they're back authenticated (see
  // markTournamentIntentBeforeLogin above), and flags that one open via
  // autoRegisterOnOpen. Resume straight to the same confirm step a real click
  // would reach — not a silent register — respecting the same guards
  // (disableRegister / cap check) handleClickRegister already applies.
  //
  // showRegister is checked explicitly first: disableRegister only covers
  // tournament-level eligibility (status/maintenance/multi-flight), not
  // whether THIS PLAYER is already registered/seated/on the waitlist — that's
  // showRegister's job, and it's the gate the real Register button's render is
  // hidden behind. Calling handleClickRegister directly bypasses that render
  // gate, so without this a player who's already registered (their own click
  // landed first, or a stale second login round-trip) would get shown
  // "Confirm Registration" again for a tournament they're already in.
  useEffect(() => {
    if (!open || !autoRegisterOnOpen) return;
    if (!showRegister) {
      onAutoRegisterConsumed?.();
      return;
    }
    handleClickRegister();
    onAutoRegisterConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoRegisterOnOpen]);

  const handleUnregister = useCallback(() => {
    if (!liveTournament) return;
    setLoading(true);
    unregister(liveTournament.tournamentId, liveTournament.gameID);
    setConfirmAction(null);
    setTimeout(() => setLoading(false), 3000);
  }, [liveTournament, unregister]);

  // Join target: SNG → tournament IS the table, so use the tournament-level gameID.
  // MTT → use the player's assigned seat (myGameID) from the per-player record.
  const isSNG = liveTournament?.gameType === TableType.SitNGo;
  const tableIdForJoin = isSNG ? liveTournament?.gameID : myGameID;
  const hasJoinableTarget =
    tableIdForJoin !== undefined && tableIdForJoin !== null && Number(tableIdForJoin) !== -1;
  const canJoinTable = hasJoinableTarget && !isMaintenance;

  // "Finding the best seat for you…" — the player is committed to an IN-PROGRESS
  // tournament (just clicked register/late-register/re-entry, or already
  // registered) but the engine hasn't assigned them a table yet. While this
  // holds, the footer shows ONE disabled progress button and Observe is hidden;
  // the moment a table is assigned (hasJoinableTarget) it flips to the enabled
  // "Join Table" button. SNG carries its own table gameID so it never enters
  // this state (hasJoinableTarget is already true). The transient `findingSeat`
  // flag bridges the brief gap between the click and the engine updating
  // myStatus; server state (myStatus P/R, no seat) keeps it true after that.
  const isFindingSeat =
    liveTournament?.status === 'P' &&
    !hasJoinableTarget &&
    (findingSeat || myStatus === 'P' || myStatus === 'R');

  const handleJoinTable = useCallback(() => {
    if (!liveTournament || !hasJoinableTarget) return;
    // Open the player's table directly — identical to tapping the SNG/MTT preview
    // card in "Your Tables". SNG → the tournament's own table
    // (liveTournament.gameID); MTT → the player's assigned seat (myGameID). The
    // player is already registered, so there is NO JOIN round-trip — the old
    // SNG path emitted join_mtt, whose response returns -1 for a not-yet-startable
    // SNG and dumped the player on the dead /table/-1 felt. `hasJoinableTarget`
    // guarantees a real table id here, and opening with observe='no' lets the
    // table's GameInner seat the player on mount, exactly as the preview card does.
    onClose();
    openTable(String(tableIdForJoin), liveTournament.game || 'texas', 'no');
  }, [liveTournament, hasJoinableTarget, tableIdForJoin, onClose, openTable]);

  if (!liveTournament) return null;

  const status = liveTournament.status || 'A';
  const colors = statusColors[status] || statusColors.A;

  // Registration rejected — a clear failure modal, matching Unity. Takes
  // priority over the detail view so the player can't miss it. The
  // insufficient-funds case adds a Cashier shortcut; every other rejection
  // (e.g. tournament full) just offers Close.
  if (registerError) {
    const clearError = () => {
      setRegisterError('');
      setRegisterErrorFunds(false);
    };
    // A rematch's Cancel / Close / X returns to the result offer (EN-11109).
    const dismissError = () => {
      clearError();
      rematch?.onCancel();
    };
    return (
      <GameModal open={open} onClose={dismissError} title={registerErrorTitle} forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <ErrorOutlineIcon
            style={{ fontSize: '2.5rem', color: '#e05555', marginBottom: '0.5rem' }}
          />
          <div style={registerErrorTextStyle}>{registerError}</div>
        </div>
        <div style={btnRowStyle}>
          {registerErrorFunds ? (
            <>
              <button
                style={primaryBtnStyle}
                onClick={() => {
                  clearError();
                  onClose();
                  openCashier('deposit');
                }}>
                Cashier
              </button>
              <button style={secondaryBtnStyle} onClick={dismissError}>
                Cancel
              </button>
            </>
          ) : (
            <button style={{ ...primaryBtnStyle, flex: 1 }} onClick={dismissError}>
              Close
            </button>
          )}
        </div>
      </GameModal>
    );
  }

  // What a re-entry costs, feeding both the "Re-entry Fee" info row and the
  // confirmation dialog — one value so the two can never disagree. Mirrors the
  // engine's rebuy charge (apps/engine/messages/inbound.js, case 'rebuy'):
  // rebuyPrice + entryFee, plus the bounty on a Bounty tournament, which the
  // engine debits and books as its own RE_ENTRY_BOUNTY_FEE ledger row.
  // A re-entry is never charged the buy-in.
  const reentryFee =
    Number(liveTournament.rebuyPrice || 0) +
    Number(liveTournament.entryFee || 0) +
    (liveTournament.gameType === 'Bounty' ? Number(liveTournament.bountyAmount || 0) : 0);

  // EN-11109: a rematch registered at the next tournament, waiting for its
  // table's id. Cancel / X returns to the offer.
  if (rematch && rematchAwaitingTable) {
    return (
      <GameModal
        open={open}
        onClose={cancelRematchAwaitingTable}
        title='Opening your table'
        forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <EmojiEventsIcon
            style={{ fontSize: '2.5rem', color: '#E98C26', marginBottom: '0.5rem' }}
          />
          <div role='status' style={registerErrorTextStyle}>
            You&apos;re registered. Opening your table…
          </div>
        </div>
        <div style={btnRowStyle}>
          <button style={{ ...secondaryBtnStyle, flex: 1 }} onClick={cancelRematchAwaitingTable}>
            Cancel
          </button>
        </div>
      </GameModal>
    );
  }

  // EN-11109: the rematch's table filled up and no next one is listed yet.
  // Cancel / X returns to the offer.
  if (rematch && rematchSearching) {
    return (
      <GameModal open={open} onClose={cancelRematchSearch} title='Finding your table' forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <EmojiEventsIcon
            style={{ fontSize: '2.5rem', color: '#E98C26', marginBottom: '0.5rem' }}
          />
          <div role='status' style={registerErrorTextStyle}>
            That table filled up. Finding you the next one…
          </div>
        </div>
        <div style={btnRowStyle}>
          <button style={{ ...secondaryBtnStyle, flex: 1 }} onClick={cancelRematchSearch}>
            Cancel
          </button>
        </div>
      </GameModal>
    );
  }

  // EN-11109: the rematch gave up looking for a table (moveRematchOn). Go to
  // lobby opens the lobby's Sit & Go tab; Close / X returns to the offer.
  if (rematch && rematchNoOpenTable) {
    return (
      <GameModal
        open={open}
        onClose={cancelRematchSearch}
        title={NO_OPEN_TABLE_NOTICE.title}
        forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <ErrorOutlineIcon
            style={{ fontSize: '2.5rem', color: '#E98C26', marginBottom: '0.5rem' }}
          />
          <div role='status' style={registerErrorTextStyle}>
            {NO_OPEN_TABLE_NOTICE.message}
          </div>
        </div>
        <div style={btnRowStyle}>
          <button
            style={{ ...primaryBtnStyle, flex: 1 }}
            onClick={() => {
              rematchDoneRef.current = true;
              setRematchNoOpenTable(false);
              (rematch.onNoOpenTable ?? rematch.onCancel)();
            }}>
            Go to lobby
          </button>
          <button style={{ ...secondaryBtnStyle, flex: 1 }} onClick={cancelRematchSearch}>
            Close
          </button>
        </div>
      </GameModal>
    );
  }

  // Confirmation sub-view
  if (confirmAction) {
    const isReg = confirmAction === 'register';
    // A rematch's Cancel / X returns to the result offer (EN-11109).
    const cancelConfirm = () => {
      setConfirmAction(null);
      setRematchRetry(false);
      rematch?.onCancel();
    };
    return (
      <GameModal
        open={open}
        onClose={cancelConfirm}
        title={isReg ? 'Confirm Registration' : 'Confirm Unregister'}
        forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <EmojiEventsIcon
            style={{ fontSize: '2.5rem', color: '#E98C26', marginBottom: '0.5rem' }}
          />
          {isReg && rematch && rematchRetry && (
            <div role='status' style={rematchRetryStyle}>
              That registration didn&apos;t go through. Confirm to try again.
            </div>
          )}
          {isReg && (
            <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
              You are about to register to{' '}
              {/* Name the tournament the player actually picked. Falls back to the
                  game-type wording when the feed has no title yet. */}
              {tournamentName ||
                (liveTournament.gameType === TableType.SitNGo ? 'Sit & Go' : 'the Multi-Table')}
              . Your account will be charged:{' '}
              <strong style={{ color: '#E98C26' }}>
                {currencyFormatter(isReentry ? reentryFee : liveTournament.buyIn || 0)}
              </strong>
            </div>
          )}
          {!isReg && (
            <>
              <div
                style={{
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.8)',
                  marginBottom: '0.25rem'
                }}>
                {tournamentName || 'Tournament'}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                Your buy-in of <strong>{currencyFormatter(liveTournament.buyIn || 0)}</strong> will
                be refunded.
              </div>
            </>
          )}
        </div>
        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={cancelConfirm}>
            Cancel
          </button>
          <button
            style={isReg ? primaryBtnStyle : dangerBtnStyle}
            onClick={isReg ? handleRegister : handleUnregister}
            disabled={loading}>
            {loading ? 'Processing...' : isReg ? 'Confirm' : 'Unregister'}
          </button>
        </div>
      </GameModal>
    );
  }

  // Registered, but the tournament reached its start time without enough
  // players to begin — tell the player why it's stuck on "STARTING ASAP".
  // A dismissible notice (Close falls through to the normal detail view),
  // consistent with the registerError / confirmAction sub-views above.
  // SnGs open their details directly, even with an old scheduled start time.
  // Recheck eligibility so a notice left over from another tournament cannot show.
  if (showWaitingModal && isWaitingForPlayers) {
    return (
      <GameModal open={open} onClose={dismissWaitingModal} title='Notification' forcePortrait>
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <PeopleIcon style={{ fontSize: '2.5rem', color: '#E98C26', marginBottom: '0.5rem' }} />
          <div style={registerErrorTextStyle}>
            Registered players for {tournamentName || 'this tournament'} have not met the minimum.
            Waiting for additional players.
          </div>
        </div>
        <div style={btnRowStyle}>
          <button style={{ ...primaryBtnStyle, flex: 1 }} onClick={dismissWaitingModal}>
            Close
          </button>
        </div>
      </GameModal>
    );
  }

  // ── Hero countdown / status text ──
  const startAt = Number(liveTournament.startAtTime || 0);
  const secsUntilStart = startAt > 0 ? startAt - now : 0;
  const heroBlock = (() => {
    if (status === 'F') return { primary: 'ENDED', secondary: '', tone: '#999' };
    if (status === 'C') return { primary: 'CANCELLED', secondary: '', tone: '#e74c3c' };
    if (status === 'P') {
      // Hero now reads "RUNNING FOR Xh Ym" with a "Started at <date>" subline,
      // matching the rest of the countdown copy. Falls back to the level/break
      // hint when we don't have a usable start timestamp.
      if (startAt > 0) {
        const elapsed = Math.max(0, now - startAt);
        const days = Math.floor(elapsed / 86400);
        const hours = Math.floor((elapsed % 86400) / 3600);
        const mins = Math.floor((elapsed % 3600) / 60);
        const primary = `RUNNING FOR ${formatHeroCountdownParts(days, hours, mins)}`;
        const startedAtText = new Date(startAt * 1000).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return { primary, secondary: `Started at ${startedAtText}`, tone: '#E98C26' };
      }
      const lvl = liveTournament.currentLevel;
      const levelSecondsLeft = getLevelSecondsLeft(liveTournament, now);
      const primary =
        levelSecondsLeft !== null
          ? `RUNNING FOR ${formatHeroRuntime(levelSecondsLeft)}`
          : 'IN PROGRESS';
      const lvlText = liveTournament.isMultiFlightBreak
        ? 'On break'
        : Number(lvl) > 0
        ? `Level ${lvl}`
        : 'In progress';
      return { primary, secondary: lvlText, tone: '#E98C26' };
    }
    if (startAt <= 0) {
      return {
        primary: 'START ANYTIME',
        secondary: 'Joins as soon as enough players register',
        tone: '#58d68d'
      };
    }
    if (secsUntilStart <= 0) {
      return { primary: 'STARTING ASAP', secondary: '', tone: '#E98C26' };
    }
    const days = Math.floor(secsUntilStart / 86400);
    const hours = Math.floor((secsUntilStart % 86400) / 3600);
    const mins = Math.floor((secsUntilStart % 3600) / 60);
    const secs = Math.floor(secsUntilStart % 60);
    const primary = `IN ${formatHeroCountdownParts(days, hours, mins, secs)}`;

    // Status A shows registration open time (Unity: StartAtTime - RegistrationOpens * 60).
    // Status R shows game start time. Other states fall back to game start time.
    // A projected occurrence (EN-11016) carries its own opening time, or 0 when
    // it opens as soon as the real instance appears. One whose window waits on
    // the game before it carries that game's start instead: it opens once that
    // game finishes, which is not known ahead.
    if (isProjected) {
      const opensAt = Number(liveTournament.registrationOpensAt || 0);
      const when = new Date(opensAt * 1000).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const opensText =
        opensAt <= 0
          ? 'Registration opens soon'
          : liveTournament.registrationOpensAfterPrevious
          ? `Registration opens after the ${when} game`
          : `Registration starts on ${when}`;
      return { primary, secondary: opensText, tone: '#E98C26' };
    }
    const registrationOpens = Number(liveTournament.registrationOpens || 0);
    const dateTimestamp =
      status === 'A' && registrationOpens > 0 ? startAt - registrationOpens * 60 : startAt;
    const dateText = new Date(dateTimestamp * 1000).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const prefix =
      status === 'A' ? 'Registration starts on ' : status === 'R' ? 'Game starts on ' : '';
    const secondary = `${prefix}${dateText}`;
    return { primary, secondary, tone: '#E98C26' };
  })();

  const prizePoolValue =
    (showSngPayouts ? payoutPool(payouts.rows) : undefined) ??
    Number(liveTournament.prizePool || 0);
  const guaranteeValue = Number(liveTournament.guarantee || 0);
  // For satellites the broadcast pre-computes seat count and ships it on
  // `satelliteSeatValue` (Math.floor(prizePool / per-seat-cost)), overwriting
  // the original per-seat dollar value. Display rules:
  //   - always show the guarantee row when isSatellite (server-trusted, no min)
  //   - pluralize with `> 1` so 0 and 1 both render "Seat"
  const isSatellite = liveTournament?.isSatellite === 1;
  const satelliteSeats = Number(liveTournament?.satelliteSeatValue || 0);
  // Satellite card (Unity parity): a "{name} @ {location}" headline plus a
  // "{description} - {liveStart} - {liveEnd}" subline. The engine resolves the
  // venue on `satelliteName` (lobby-list query) or `satelliteAt` (details
  // query) and ships the live-schedule window as liveStart / liveEnd.
  const satelliteLocation = liveTournament?.satelliteName || liveTournament?.satelliteAt || '';
  // Lobby broadcast ships raw epoch columns (satelliteLiveSchedule /
  // satelliteLiveScheduleEnd); other queries expose formatted liveStart /
  // liveEnd. Read the epoch columns first, fall back to the formatted aliases.
  const satelliteStart = formatSatelliteDate(
    liveTournament?.satelliteLiveSchedule ?? liveTournament?.liveStart
  );
  const satelliteEnd = formatSatelliteDate(
    liveTournament?.satelliteLiveScheduleEnd ?? liveTournament?.liveEnd
  );
  const satelliteRange =
    satelliteStart && satelliteEnd
      ? `${satelliteStart} - ${satelliteEnd}`
      : satelliteStart || satelliteEnd || '';
  const satelliteTitle = [liveTournament?.tablename, satelliteLocation].filter(Boolean).join(' @ ');
  const satelliteSubtitle = [liveTournament?.satelliteDescription, satelliteRange]
    .filter(Boolean)
    .join(' - ');
  const showSatelliteCard = isSatellite && (!!satelliteTitle || !!satelliteSubtitle);
  const showGuarantee = isSatellite || guaranteeValue > 0;
  const guaranteeDisplay = isSatellite
    ? `${satelliteSeats} ${satelliteSeats > 1 ? 'Seats' : 'Seat'}`
    : currencyCompact(guaranteeValue);

  // Re-entry — mirrors Unity's TournamentAboutToStartView reentry display.
  // socket-broadcast-worker.js sends rebuys as the string 'Unlimited' when negative,
  // otherwise as a numeric count — handle both shapes.
  // Re-entry is MTT-only. Legacy SnG configurations can still contain rebuy
  // values, but these must not advertise re-entry or its chips/fee in the UI.
  const rebuysRaw = isSitNGo || liveTournament.tabletype === 't' ? 0 : liveTournament.rebuys;
  const isUnlimitedRebuys = rebuysRaw === 'Unlimited' || Number(rebuysRaw) === -1;
  const rebuysCountNum = isUnlimitedRebuys ? -1 : Number(rebuysRaw ?? 0);
  const reentryCountText = isUnlimitedRebuys
    ? 'Unlimited'
    : rebuysCountNum === 0
    ? 'N/A'
    : String(rebuysCountNum);
  // socket-broadcast-worker.js sends startingChips through numberFormatter (e.g. "1,000"),
  // while rebuyChips/reentryChips are raw numbers — strip commas before comparing.
  const toChipNumber = (v: unknown): number =>
    typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
  const effectiveReentryChips =
    liveTournament.reentryChips !== null && liveTournament.reentryChips !== undefined
      ? liveTournament.reentryChips
      : liveTournament.rebuyChips;
  const showReentryChipsRows =
    rebuysCountNum !== 0 && effectiveReentryChips !== null && effectiveReentryChips !== undefined;
  // ReentryChipsValue computes its own bonus/penalty state from the two chip
  // values it receives — a divergence sells the re-entry, equal renders flat.

  // Add On — Unity gates all three rows on addOn > 0.
  const addOnCount = Number(liveTournament.addOn || 0);
  const showAddOn = addOnCount > 0;

  return (
    <>
      <GameModal
        open={open}
        onClose={onClose}
        title={tournamentName || 'Tournament'}
        maxWidth='460px'
        forcePortrait>
        {/* Status badges row */}
        <div style={badgeRowStyle}>
          <div style={{ ...statusBadgeStyle, backgroundColor: colors.bg }}>
            <span
              style={{
                color: colors.text,
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase'
              }}>
              {statusLabels[status] || status}
            </span>
          </div>
          {liveTournament.gameType && <div style={typePillStyle}>{liveTournament.gameType}</div>}
          {liveTournament.isSatellite === 1 && <div style={satellitePillStyle}>Satellite</div>}
          {(myStatus === 'P' || myStatus === 'R') && (
            <span style={registeredBadge}>Registered</span>
          )}
        </div>

        {/* Hero countdown */}
        <div style={heroSectionStyle}>
          <div style={{ ...heroPrimaryStyle, color: heroBlock.tone }}>{heroBlock.primary}</div>
          {heroBlock.secondary && <div style={heroSecondaryStyle}>{heroBlock.secondary}</div>}
        </div>

        {/* Action buttons — placed directly under the running-status hero so the
            primary actions (Register / Late Register / Join / Tournament Info)
            are reachable at a glance, above the tournament detail rows. */}
        <div style={ctaStackStyle}>
          {isFindingSeat ? (
            // Seat assignment in progress: a single disabled progress button —
            // until the engine assigns a table, then this whole branch flips to
            // the enabled "Join Table" button below.
            <button
              style={{ ...primaryBtnStyle, opacity: 0.5, cursor: 'not-allowed', width: '100%' }}
              disabled
              aria-busy>
              Finding the best seat for you…
            </button>
          ) : (
            <>
              {showRegister && (
                <button
                  style={
                    !guest && disableRegister
                      ? {
                          ...primaryBtnStyle,
                          ...ctaStackBtnStyle,
                          opacity: 0.4,
                          cursor: 'not-allowed'
                        }
                      : { ...primaryBtnStyle, ...ctaStackBtnStyle }
                  }
                  onClick={handleClickRegister}
                  disabled={!guest && (disableRegister || loading)}>
                  {guest ? guestRegisterLabel : registerLabel}
                </button>
              )}
              {showJoinTable && (
                <button
                  style={{ ...primaryBtnStyle, ...ctaStackBtnStyle }}
                  onClick={handleJoinTable}
                  disabled={!canJoinTable}
                  title={
                    !hasJoinableTarget && myStatus === 'P'
                      ? 'Waiting for seat assignment'
                      : undefined
                  }>
                  Join Table
                </button>
              )}
              {/* Opens the full ranking / prizes / tables / blinds dashboard
                (replaces the old Observe button; observing a live felt now
                lives inside that panel's Tables tab). Guests are hidden from
                this entirely — the Tables tab's Observe button is how a
                guest would otherwise reach a tournament felt, and guests
                must not observe tournament tables. */}
              {/* A projected occurrence has no ranking / tables / live level for
                  the dashboard to show, and its id is not one the engine knows. */}
              {!guest && !isProjected && (
                <button
                  style={{ ...tournamentInfoBtnStyle, ...ctaStackBtnStyle }}
                  onClick={() => {
                    // Evict any cached gotTnmStatus rows for this tournament before
                    // opening, so a RESTARTED tournament (same id, fresh instance)
                    // can't show the finished prior instance's state. The dashboard
                    // then repopulates from the live feed via its own subscribe.
                    dispatch(clearTournamentStatusFor(liveTournament?.tournamentId));
                    setShowInfo(true);
                  }}>
                  Tournament Info
                </button>
              )}
              {showUnregister && (
                <button
                  style={{ ...dangerBtnStyle, ...ctaStackBtnStyle }}
                  onClick={() => setConfirmAction('unregister')}
                  disabled={loading}>
                  Unregister
                </button>
              )}
            </>
          )}
        </div>

        {/* Inline result message — feedback for the action buttons above. */}
        {resultMsg && <div style={resultMsgStyle}>{resultMsg}</div>}

        {/* Satellite card (Unity parity) — headline "{name} @ {location}" and a
          "{description} - {schedule}" subline, sat just under the hero. */}
        {showSatelliteCard && (
          <div style={satelliteCardStyle}>
            <span style={satelliteCardPillStyle}>SATELLITE</span>
            <div style={satelliteCardBodyStyle}>
              {satelliteTitle && <span style={satelliteCardTitleStyle}>{satelliteTitle}</span>}
              {satelliteSubtitle && <span style={satelliteCardSubStyle}>{satelliteSubtitle}</span>}
            </div>
          </div>
        )}

        {/* Stat tiles — money + players */}
        <div style={tileGridStyle}>
          <StatTile
            label='Buy-in'
            value={currencyCompact(liveTournament.buyIn || 0)}
            accent='#E98C26'
          />
          <StatTile
            label={showSngPayouts ? 'Prize Pool' : 'Current prize pool'}
            value={
              showSngPayouts ? currencyFormatter(prizePoolValue) : currencyCompact(prizePoolValue)
            }
            accent='#58d68d'
          />
          {/* Entry count only, no " / maxPlayers" cap. The cap told the player nothing
              useful about whether to register and made a healthy field look empty
              on large-field events. Uncapped tournaments (maxPlayers === 0) always
              rendered the bare count, so capped and uncapped now match, and Unity,
              the parity baseline, shows the bare count on its MTT cells too. This is
              display-only: the server still closes registration when a cap fills. */}
          <StatTile
            label='Entries'
            value={String(liveTournament.registeredPlayers || 0)}
            icon={
              <PeopleIcon
                style={{
                  fontSize: '0.9rem',
                  verticalAlign: 'middle',
                  marginRight: 4,
                  color: 'rgba(255,255,255,0.55)'
                }}
              />
            }
          />
          <StatTile
            label='Starting Chips'
            value={(liveTournament.startingChips || 0).toLocaleString()}
          />
        </div>

        {showSngPayouts && (
          <TournamentPayouts
            {...payouts}
            seats={Number(liveTournament.maxPlayers ?? liveTournament.maxSeat)}
            bountyAmount={liveTournament.bountyAmount}
          />
        )}

        {/* Late Registration status — surfaced here so the player sees at a
            glance whether they can still late-register, without opening the
            Tournament Info dashboard. Shown for in-progress MTTs that offer late
            entry (SNGs have no late-reg window). Derived from the SAME state as
            the Late Register button below, so the two never disagree. */}
        {hasLateEntry && !isSitNGo && (
          <div style={infoRowStyle}>
            <span style={modalLabelStyle}>Late Registration</span>
            <span
              style={{
                ...modalValueStyle,
                color: isLateRegister ? '#58d68d' : 'rgba(255, 255, 255, 0.55)'
              }}>
              {lateRegSecondsLeft !== null
                ? lateRegSecondsLeft > 0
                  ? humanizeCountdown(lateRegSecondsLeft)
                  : 'Ended'
                : isLateRegister
                ? 'Open'
                : 'Ended'}
            </span>
          </div>
        )}

        <div style={infoRowStyle}>
          <span style={modalLabelStyle}>Level</span>
          <span style={modalValueStyle}>
            {status === 'R' || status === 'A'
              ? 'Pre-registration'
              : Number(liveTournament.lvlDisplay) === 0
              ? 'Break'
              : String(liveTournament.lvlDisplay ?? '')}
          </span>
        </div>

        {/* "Started at <date>" lives in the hero subline once the tournament is in
          progress, so the dedicated detail row is dropped for status === 'P'
          to avoid showing the same timestamp twice. */}
        {status !== 'P' &&
          (Number(liveTournament.startAtTime) > 0 ? (
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Starts</span>
              <span style={modalValueStyle}>
                {new Date(Number(liveTournament.startAtTime) * 1000).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
          ) : (
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Starts</span>
              <span style={modalValueStyle}>Anytime</span>
            </div>
          ))}

        {showGuarantee && (
          <div style={infoRowStyle}>
            <span style={modalLabelStyle}>Guarantee</span>
            <span style={modalValueStyle}>{guaranteeDisplay}</span>
          </div>
        )}

        <div style={infoRowStyle}>
          <span style={modalLabelStyle}>Re-entry</span>
          <span style={modalValueStyle}>{reentryCountText}</span>
        </div>

        {showReentryChipsRows && (
          <>
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Re-entry Chips</span>
              <AnimatedChipsValue chips={toChipNumber(effectiveReentryChips)} />
            </div>
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Re-entry Fee</span>
              <span style={modalValueStyle}>{currencyCompact(reentryFee)}</span>
            </div>
          </>
        )}

        {showAddOn && (
          <>
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Add On</span>
              <span style={modalValueStyle}>{addOnCount}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Add On Fee</span>
              <span style={modalValueStyle}>
                {currencyCompact(Number(liveTournament.addOnPrice || 0))}
              </span>
            </div>
            <div style={infoRowStyle}>
              <span style={modalLabelStyle}>Add On Chips</span>
              <AnimatedChipsValue chips={Number(liveTournament.addOnChips || 0)} />
            </div>
          </>
        )}

        {!showSngPayouts && liveTournament.bountyAmount > 0 && (
          <div style={infoRowStyle}>
            <span style={modalLabelStyle}>Bounty Amount</span>
            <span style={{ ...modalValueStyle, color: '#E98C26' }}>
              {currencyCompact(liveTournament.bountyAmount)}
            </span>
          </div>
        )}
      </GameModal>
      {showInfo &&
        // Portal to <body> above the detail modal (GameModal portals at
        // zIndex 200). The fixed, full-viewport wrapper also gives the panel's
        // absolute backdrop a viewport-sized positioned ancestor so it covers
        // the whole screen from the lobby, not just this modal's box.
        // ForcePortrait lives INSIDE the portal so the Tournament Info dashboard
        // rotates WITH the portrait-locked lobby on mobile-landscape (the portal
        // escapes the lobby's own wrapper, so the rotation has to be here).
        ReactDOM.createPortal(
          <ForcePortrait>
            <div style={infoOverlayStyle}>
              <TournamentDetails
                open
                onClose={() => setShowInfo(false)}
                tournamentId={Number(liveTournament.tournamentId)}
                gameTitle={tournamentName || 'Tournament'}
                tableType={liveTournament.tabletype || (isSitNGo ? 't' : 'm')}
                tournamentStatus={status}
                tournamentPrizePool={prizePoolValue}
                lobbyAddOn={{
                  count: liveTournament.addOn,
                  price: liveTournament.addOnPrice,
                  chips: liveTournament.addOnChips,
                  schedule: liveTournament.schedule,
                  isFinalMultiFlight: liveTournament.isFinalMultiFlight
                }}
                sngPayouts={showSngPayouts ? payouts : undefined}
                maxSeats={Number(liveTournament.maxPlayers ?? liveTournament.maxSeat)}
                bountyAmount={liveTournament.bountyAmount}
                forcePortrait
              />
            </div>
          </ForcePortrait>,
          document.body
        )}
    </>
  );
};

/**
 * Animated chips value used for the Re-entry Chips and Add On Chips rows.
 * Always runs the same celebration: glyphs are filled with a moving
 * gold/orange gradient (shimmer sweep), wear a slow breathing glow, and the
 * whole value heartbeats. No comparison branch — both rows render
 * identically regardless of whether the count is above or below the
 * tournament's starting stack. The visual cue says "this is worth your
 * attention" without making a +/- value judgement on the player's behalf.
 */
const AnimatedChipsValue = ({ chips }: { chips: number }) => {
  const numberRef = useRef<HTMLSpanElement>(null);
  const { reduceAnimations } = useAnimationPreferences();

  useEffect(() => {
    const el = numberRef.current;
    // EN-11003: reduced animations keep the gold gradient but drop the
    // heartbeat, shimmer and breathing glow.
    if (!el || reduceAnimations) return;

    const tweens: gsap.core.Tween[] = [];

    // Heartbeat scale — anchored to the right edge so the right-alignment
    // of the value column stays stable while it pulses.
    tweens.push(
      gsap.to(el, {
        scale: 1.16,
        duration: 0.9,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        transformOrigin: 'right center',
        delay: 0.5
      })
    );

    // Gradient shimmer: gradient is wider than the text and slides
    // continuously, painting a moving highlight across the glyphs.
    gsap.set(el, { backgroundPosition: '-100% center' });
    tweens.push(
      gsap.to(el, {
        backgroundPosition: '200% center',
        duration: 2.6,
        repeat: -1,
        ease: 'sine.inOut',
        delay: 0.6
      })
    );

    // Slow breathing glow under the glyphs so the shimmer reads against an
    // aura instead of floating on flat dark.
    tweens.push(
      gsap.fromTo(
        el,
        { textShadow: '0 0 4px rgba(233, 140, 38, 0.3)' },
        {
          textShadow: '0 0 14px rgba(233, 140, 38, 0.75), 0 0 22px rgba(240, 201, 126, 0.5)',
          duration: 1.1,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut'
        }
      )
    );

    return () => {
      tweens.forEach((t) => t.kill());
      gsap.set(el, { clearProps: 'textShadow,backgroundPosition,transform' });
    };
  }, [reduceAnimations]);

  const numberStyle: CSSProperties = {
    fontWeight: 800,
    background:
      'linear-gradient(90deg, #E98C26 0%, #f0c97e 25%, #ffeaa0 50%, #f0c97e 75%, #E98C26 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    display: 'inline-block'
  };

  return (
    <span style={modalValueStyle}>
      <span ref={numberRef} style={numberStyle}>
        {chips.toLocaleString()}
      </span>
    </span>
  );
};

const StatTile = ({
  label,
  value,
  accent,
  subtitle,
  icon
}: {
  label: string;
  value: React.ReactNode;
  accent?: string;
  subtitle?: string;
  icon?: React.ReactNode;
}) => (
  <div style={statTileStyle}>
    <div style={statTileLabel}>{label}</div>
    <div style={{ ...statTileValue, ...(accent ? { color: accent } : {}) }}>
      {icon}
      {value}
    </div>
    {subtitle && <div style={statTileSubtitle}>{subtitle}</div>}
  </div>
);

export default TournamentDetailModal;

// Full-viewport, fixed overlay that hosts the Tournament Info dashboard above
// the detail modal (GameModal portals at zIndex 200, so this sits at 300).
const infoOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 300
};

// "Tournament Info" — a distinct secondary CTA. The shared secondaryBtnStyle's
// dark fill blended into the dark info tiles below it, so it didn't read as a
// button. An orange OUTLINE (tinted fill + orange border/text) marks it clearly
// as tappable while staying visibly different from the solid-orange primary
// (Register / Late Register) so the two don't look like the same action.
// The detail screen's action buttons as full-width rows, top to bottom:
// Register / Join Table, Tournament Info, then Unregister.
const ctaStackStyle: CSSProperties = {
  ...btnRowStyle,
  flexDirection: 'column'
};
const ctaStackBtnStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '100%'
};

const tournamentInfoBtnStyle: CSSProperties = {
  ...secondaryBtnStyle,
  background: 'rgba(233, 140, 38, 0.12)',
  color: '#E98C26',
  border: '1px solid rgba(233, 140, 38, 0.6)'
};

const statusBadgeStyle: CSSProperties = {
  padding: '3px 10px',
  borderRadius: '6px',
  display: 'inline-flex',
  alignItems: 'center'
};

const registeredBadge: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#58d68d',
  backgroundColor: 'rgba(39, 174, 96, 0.15)',
  padding: '2px 8px',
  borderRadius: '4px'
};

const registerErrorTextStyle: CSSProperties = {
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.85)',
  lineHeight: 1.5,
  padding: '0 0.25rem'
};

const rematchRetryStyle: CSSProperties = {
  ...registerErrorTextStyle,
  color: '#E98C26',
  marginBottom: '0.5rem'
};

const resultMsgStyle: CSSProperties = {
  fontSize: '0.8rem',
  color: '#58d68d',
  textAlign: 'center',
  padding: '8px 12px',
  backgroundColor: 'rgba(39, 174, 96, 0.1)',
  borderRadius: '8px',
  border: '1px solid rgba(39, 174, 96, 0.15)',
  marginTop: '0.5rem'
};

const badgeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  flexWrap: 'wrap',
  marginBottom: '0.5rem'
};

const typePillStyle: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#E98C26',
  backgroundColor: 'rgba(233, 140, 38, 0.12)',
  border: '1px solid rgba(233, 140, 38, 0.25)',
  padding: '2px 8px',
  borderRadius: '4px'
};

const satellitePillStyle: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#5dade2',
  backgroundColor: 'rgba(41, 128, 185, 0.15)',
  border: '1px solid rgba(41, 128, 185, 0.25)',
  padding: '2px 8px',
  borderRadius: '4px'
};

/* Satellite card under the hero (Unity's gold satellite card). */
const satelliteCardStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.75rem 0.9rem',
  margin: '0 0 0.9rem',
  borderRadius: '12px',
  backgroundColor: 'rgba(233, 200, 90, 0.14)',
  border: '1px solid rgba(233, 200, 90, 0.35)'
};

const satelliteCardPillStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: '#12100c',
  backgroundColor: '#e8c85a',
  padding: '4px 10px',
  borderRadius: '999px'
};

const satelliteCardBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0
};

const satelliteCardTitleStyle: CSSProperties = {
  fontSize: '0.88rem',
  fontWeight: 700,
  color: '#f1e3c7'
};

const satelliteCardSubStyle: CSSProperties = {
  fontSize: '0.72rem',
  color: 'rgba(241, 227, 199, 0.65)',
  lineHeight: 1.4
};

const heroSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25rem',
  padding: '1rem 0.75rem',
  marginBottom: '0.75rem',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(233, 140, 38, 0.08), rgba(212, 168, 83, 0.04))',
  border: '1px solid rgba(212, 168, 83, 0.18)'
};

const heroPrimaryStyle: CSSProperties = {
  fontSize: '1.65rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  fontFamily: "'Rye', 'Outfit', serif",
  textAlign: 'center',
  lineHeight: 1.1,
  textShadow: '0 1px 8px rgba(233, 140, 38, 0.25)'
};

const heroSecondaryStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.55)',
  textAlign: 'center',
  letterSpacing: '0.02em'
};

const tileGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem',
  marginBottom: '0.85rem'
};

const statTileStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  padding: '0.7rem 0.85rem',
  borderRadius: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.06)'
};

const statTileLabel: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};

const statTileValue: CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.95)',
  letterSpacing: '0.01em'
};

const statTileSubtitle: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255, 255, 255, 0.4)',
  fontWeight: 500
};
